
import { UserManager, User, UserManagerSettings } from "oidc-client-ts";
import { useGlobalFeaturesStore } from "store/GlobalFeaturesStore";
import { useFeUserStore } from "store/FeUserStore";

// Constants
const IMPERSONATE_COOKIE = "IMPERSONATE_COOKIE";
const hostName = window.location.hostname;
const baseUrl = `${window.location.protocol}//${window.location.hostname}${
  window.location.port ? `:${window.location.port}` : ""
}`;
// baseUrl example = https://localhost:5003

const authorityDictionary: Map<string, string> = new Map([
  // PROD
  ["app.zenithpartners.com.au", "https://auth.fefundinfo.com/"],
  ["failover-app.zenithpartners.com.au", "https://auth.fefundinfo.com/"],
  ["app-swa.zenithpartners.com.au", "https://auth.fefundinfo.com/"], // SWA
  ["staging-app.zenithpartners.com.au", "https://auth.fefundinfo.com/"], // interim, until the cutover
  // TEST
  ["test-app.zenithpartners.com.au", "https://prep-auth.fefundinfo.com/"],
  ["test-app-swa.zenithpartners.com.au", "https://prep-auth.fefundinfo.com/"], // SWA
  ["staging-test-app.zenithpartners.com.au", "https://prep-auth.fefundinfo.com/"], // interim, until the cutover
  // DEV
  ["localhost", "https://prep-auth.fefundinfo.com/"],
  ["dev-app.zenithpartners.com.au", "https://prep-auth.fefundinfo.com/"],
  ["staging-dev-app.zenithpartners.com.au", "https://prep-auth.fefundinfo.com/"], // interim, until the cutover
]);

const clientIdDictionary: Map<string, string> = new Map([
  // PROD
  ["app.zenithpartners.com.au", "zenithmosaic-webapp-prod"],
  ["app-swa.zenithpartners.com.au", "zenithmosaic-webapp-prod"],
  ["failover-app.zenithpartners.com.au", "zenithmosaic-webapp-prod"],
  ["staging-app.zenithpartners.com.au", "zenithmosaic-webapp-prod"], // interim, until the cutover
  // TEST
  ["test-app.zenithpartners.com.au", "zenithmosaic-webapp-test"],
  ["test-app-swa.zenithpartners.com.au", "zenithmosaic-webapp-test"],
  ["staging-test-app.zenithpartners.com.au", "zenithmosaic-webapp-test"], // interim, until the cutover
  // DEV
  ["localhost", "zenithmosaic-webapp-dev"],
  ["dev-app.zenithpartners.com.au", "zenithmosaic-webapp-dev"],
  ["staging-dev-app.zenithpartners.com.au", "zenithmosaic-webapp-dev"], // interim, until the cutover
]);

// Vite env overrides (e.g. the compose Docker stack points these at the local
// IdentityServer). Unset, behaviour falls back to the per-hostname dictionaries.
const config: UserManagerSettings = {
  authority:
    import.meta.env.VITE_IDENTITY_AUTHORITY ||
    authorityDictionary.get(hostName)!,
  client_id:
    import.meta.env.VITE_IDENTITY_CLIENT_ID ||
    clientIdDictionary.get(hostName)!,
  redirect_uri: `${baseUrl}/au/callback`,
  response_type: "code",
  scope: import.meta.env.VITE_IDENTITY_SCOPE || "openid profile offline_access",
  silent_redirect_uri: `${baseUrl}/au/callbacksilent`,
  automaticSilentRenew: true,
  post_logout_redirect_uri: baseUrl,
  // Default true. Compose points at the local IdentityServer and sets this
  // "false": the SPA gets sub from the id_token and roles from the decoded
  // access token, so the extra cross-origin /connect/userinfo hop is not
  // needed and only adds a failure mode locally.
  loadUserInfo: import.meta.env.VITE_IDENTITY_LOAD_USERINFO !== "false",
};

class CustomUserManager extends UserManager {
  constructor(config: UserManagerSettings) {
    super(config);
  }

  async getUser(): Promise<User | null> {
    let localUser = await super.getUser();

    if (localUser) {
      localUser = await this.getManipulatedProfile(localUser);
    }

    return Promise.resolve(localUser);
  }

  async signinRedirect(args?: any): Promise<void> {
    return super.signinRedirect(args);
  }

  async signinSilent(): Promise<User | null> {
    return super.signinSilent();
  }

  async removeUser(): Promise<void> {
    return super.removeUser();
  }

  // Since we used Claims for the user licence, we need to manipulate the user profile
  // to get the user licence from the claims and set it to the user profile (roles)
  private async getManipulatedProfile(user: User): Promise<User> {
    const feUserStore = await useFeUserStore.getState();

    if (feUserStore.impersonateUser.userId !== 0) {
      // See if this staff is impersonating a user, if so, set the impersonated user's details
      const impesonateId = this.getCookie(IMPERSONATE_COOKIE);
      if (impesonateId != null) {
        const impersonateUser = user;
        impersonateUser.profile.role = feUserStore.impersonateUser.licences;
        if (Array.isArray(impersonateUser.profile.role)) {
          impersonateUser.profile.role.push("staff");
        }
        impersonateUser.profile.client_name =
          feUserStore.impersonateUser.clientName;
        impersonateUser.profile.sub =
          feUserStore.impersonateUser.userId.toString();
        impersonateUser.profile.profile_clientid =
          feUserStore.impersonateUser.clientId;
        impersonateUser.profile.email = feUserStore.impersonateUser.email;
        impersonateUser.profile.name = feUserStore.impersonateUser.name;
        impersonateUser.profile.preferred_username =
          feUserStore.impersonateUser.email;
        impersonateUser.profile.IsImpersonating = true;
        impersonateUser.profile.OriginalSub =
          feUserStore.feUser.userId.toString();
        return impersonateUser;
      }

      return user;
    }

    const localUser = user;
    let decodedAccessToken = this.decodeJWTToken(localUser.access_token);
    localUser.profile.role = decodedAccessToken.payload.role;
    if (feUserStore.userLoaded) {
      localUser.profile.name = feUserStore.feUser.name;
      localUser.profile.client_name = feUserStore.feUser.clientName;
      localUser.profile.sub = feUserStore.feUser.userId?.toString();
      localUser.profile.profile_clientid = feUserStore.feUser.clientId;
    }
    return localUser;
  }

  private getCookie(cookieName: string) {
    const name = cookieName + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(";");
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === " ") {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length, cookie.length);
      }
    }
    return "";
  }

  private decodeJWTToken(token: string) {
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error(
        "Invalid JWT format. Expected three parts separated by periods."
      );
    }
    // Decode each part and return them
    const decodedHeader = JSON.parse(atob(parts[0]));
    const decodedPayload = JSON.parse(atob(parts[1]));
    const signature = parts[2];
    return { header: decodedHeader, payload: decodedPayload, signature };
  }
}

// Initialise the user manager
let userManager = new CustomUserManager(config);

// Add event listeners
userManager.events.addAccessTokenExpired(() => {
  console.log(
    "token expired, try doing a silent renew",
    new Date().toLocaleString()
  );
  userManager
    .signinSilent()
    .then(function (user) {
      // The user's token has been silently renewed
      console.log("Token renewed successfully");
      // Optionally, store user info or tokens in your app
    })
    .catch(function (error) {
      console.error("Silent renewal failed, redirecting to login page", error);

      userManager.removeUser();
      localStorage.removeItem("fe-user-store");

      userManager.signinRedirect();
    });
});

export default userManager;
